/** @prettier */

import {Grid, Box, Button, Menu, MenuItem, Stack} from '@mui/material';
import {useState, useEffect} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Auth, Storage} from 'aws-amplify';
import {useTranslation} from 'react-i18next';
import {addTranslation} from '../functions/addTranslation';

//API import
import {patchSettingUser} from '../API/API_Collection';
//MTH component import
import {HeaderErrorList} from './my-site/HeaderErrorList';
import TextFieldSelectBox from '../component/SelectBox';
/* Style import */
import '../css/common/common.css';
//interface import
import * as apiIF from '../Type/api';

import {routeList} from './PrivateRoute';
import {file, iconBlack, pullDownMenu} from '../define';
import getLogo from '../functions/getLogo';

/**
 * ログイン後のヘッダ
 * @param props props
 * - email ユーザのメールアドレス
 * - language 表示する言語
 * - changeLocaleFunc MUIのコンポーネントで表示する言語を変更するための関数
 * - errorState エラー内容
 * - unitErrorNum エラー発生台数
 * - hccvqErrorTitle エラーヘッダーに表示するタイトル
 * - airConditionerTitle エラーヘッダーに表示するタイトル
 * @returns JSX
 */
const Header = (props: {
  email: string;
  language: apiIF.res.getSettingUser['language'];
  changeLocaleFunc: (language: apiIF.res.getSettingUser['language']) => void;
  errorState?: apiIF.res.getStatesErrorSites['error-informations'];
  unitErrorNum?: number;
  hccvqErrorTitle?: string;
  airConditionerErrorTitle?: string;
}) => {
  const [trans, internationalization] = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  //prettier-ignore
  //HCCV(Q)、Air Conditionerで異常となっている空調機の状態値
  const [errorState, setErrorState] = useState<apiIF.res.getStatesErrorSites['error-informations']>([]);
  //HCCV(Q)、Air Conditionerで異常となっている空調機の台数
  const [unitErrorNum, setUnitErrorNum] = useState(0);
  //HCCV(Q)エラー時にヘッダーに表示するタイトル
  const [hccvqErrorTitle, setHCCVQErrorTitle] = useState('');
  //prettier-ignore
  //Air Conditionerエラーがある時にヘッダーに表示するタイトル
  const [airConditionerErrorTitle, setAirConditionerErrorTitle] = useState('');
  //M-ACCESSのロゴデータ（取得できない場合はデフォルト画像のパスが入る）
  const [maccessLogo, setMaccessLogo] = useState<string>('');
  //MITSUBISHIのロゴデータ（取得できない場合はデフォルト画像のパスが入る）
  const [mhiLogo, setMHILogo] = useState<string>('');
  //prettier-ignore
  //言語設定
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>(props.language);
  //現在開いているページ（マイサイト or HELP）
  const [activeTab, setActiveTab] = useState('');

  //HELPメニュー
  const [helpMenu, setHelpMenu] = useState<null | HTMLElement>(null);
  const helpMenuOpen = Boolean(helpMenu);
  //アカウントメニュー
  const [accountMenu, setAccountMenu] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(accountMenu);

  /**
   * 現在開いているページの判定
   */
  useEffect(() => {
    //HELPページ一覧
    const helpPageList = [
      routeList.mainManual.path,
      routeList.mainTermsOfService.path,
      routeList.mainPrivacyPolicy.path,
    ];

    if (helpPageList.includes(location.pathname)) {
      //HELPページを開いている場合
      setActiveTab('help');
    } else if (location.pathname.includes(routeList.mySite.path)) {
      //MY SITESページを開いている場合
      setActiveTab('mySites');
    } else {
      setActiveTab('');
    }
  }, [location]);

  /** ロゴの読み込み処理,語設定適用処理 */
  useEffect(() => {
    (async () => {
      /** ロゴの読み込み処理 */
      await getLogo(
        logo => {
          setMaccessLogo(logo);
        },
        logo => {
          setMHILogo(logo);
        }
      );

      //ユーザ言語の読み込み処理
      if (language !== 'custom') {
        //言語設定がcustom以外の場合
        internationalization.changeLanguage(language);
      } else {
        //言語設定がcustomの場合
        Storage.configure({
          customPrefix: {private: file.LANGUAGE_S3_KEY_PREFIX},
        });
        const result = await Storage.list('', {level: 'private'});
        //言語ファイルの有無を検索
        const hasUserLang = result.results.some(info => {
          return info['key'] === file.CUSTOM_TRANSLATION_NAME;
        });
        //言語ファイルがある場合、S3内の言語ファイルを取得
        if (hasUserLang) {
          const url = await Storage.get(file.CUSTOM_TRANSLATION_NAME, {
            level: 'private',
          });
          //ファイルを読み込む
          const response = await fetch(url);
          const data = await response.text();
          addTranslation(data); //言語データを翻訳リソースに追加する
          internationalization.changeLanguage(language); //追加した翻訳を適用する
        }
      }
    })();
  }, [language]);

  /**
   * HCCV(Q)、Air Conditionerで異常となっている空調機の台数の取得
   */
  useEffect(() => {
    setErrorState(props.errorState === undefined ? [] : props.errorState);
    setUnitErrorNum(props.unitErrorNum === undefined ? 0 : props.unitErrorNum);
    setHCCVQErrorTitle(
      props.hccvqErrorTitle === undefined ? '' : props.hccvqErrorTitle
    );
    setAirConditionerErrorTitle(
      props.airConditionerErrorTitle === undefined
        ? ''
        : props.airConditionerErrorTitle
    );
  }, [
    props.errorState,
    props.unitErrorNum,
    props.hccvqErrorTitle,
    props.airConditionerErrorTitle,
  ]);

  /**
   * HELPメニューを開いた時の処理
   * @param event
   */
  const helpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHelpMenu(event.currentTarget);
  };
  /**
   * HELPメニューを閉じたときの処理
   */
  const helpClose = () => {
    setHelpMenu(null);
  };

  /**
   * Logoutボタン押下時処理
   */
  const onSignOut = async () => {
    await Auth.signOut().then(data => {
      //サインアウトしたらログイン画面に遷移
      window.location.href = routeList.login.path;
    });
  };

  /**
   * アカウントメニューを開いた時の処理
   * @param event
   */
  const accountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAccountMenu(event.currentTarget);
  };
  /**
   * アカウントメニューを閉じたときの処理
   */
  const accountClose = () => {
    setAccountMenu(null);
  };

  /**
   * 言語選択時処理
   * @param language 選択した言語
   */
  const changeLanguage = (language: apiIF.res.getSettingUser['language']) => {
    setLanguage(language);
    props.changeLocaleFunc(language);
    //言語設定をユーザ情報に保存
    patchSettingUser({language: language})
      .then(response => {
        internationalization.changeLanguage(language); //言語切替
      })
      .catch(err => {});
  };

  return (
    <Stack>
      {/* HCCV(Q)、Air Conditionerのエラーが発生中のみ表示 */}
      {unitErrorNum > 0 ? (
        <HeaderErrorList
          errorState={errorState}
          errorNum={unitErrorNum}
          hccvqErrorTitle={hccvqErrorTitle}
          airConditionerErrorTitle={airConditionerErrorTitle}
        />
      ) : (
        <></>
      )}
      <Grid
        container
        columnSpacing={3}
        justifyContent="space-between"
        alignItems="center">
        {/* M-ACCESSのロゴ */}
        <Grid item xs="auto" className="logoBox">
          <Link to={routeList.mySite.path} id="mv-linkMainHeaderMysite">
            {maccessLogo === '' ? (
              <Box className="logoBox__icon--m-accessLogo"></Box>
            ) : (
              <img
                className="logoBox__icon--m-accessLogo"
                src={maccessLogo}
                alt={`${trans('MainHeader.image.alternativeText.mAccessLogo')}`}
                id="mv-mAccessLogo"
              />
            )}
          </Link>
        </Grid>
        {/* マイサイトボタン */}
        <Grid item xs="auto">
          <Box
            className={`menuBox ${
              activeTab === 'mySites' ? 'menuBox--active' : ''
            }`}>
            <Link
              to={routeList.mySite.path}
              id="mv-linkMainHeaderMysiteTxt"
              className="menuBox__mySite">
              <Box
                className="menuBox__mySite__text"
                id="mv-linkMainHeaderMysiteBox">
                {trans('MainHeader.linkTxt.mySites')}
              </Box>
            </Link>
          </Box>
        </Grid>
        {/* ヘルプボタン */}
        <Grid item xs>
          <Box className="menuBox__help">
            <Box
              className={`menuBox ${
                activeTab === 'help' ? 'menuBox--active' : ''
              }`}>
              <Button
                color="secondary"
                id="mv-mainHeaderHelpBox"
                className="menuBox__help__button"
                aria-controls={helpMenuOpen ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                aria-expanded={helpMenuOpen ? 'true' : undefined}
                onClick={helpClick}
                endIcon={
                  <img
                    className="logoBox__icon--arrow"
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `MainHeader.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }>
                {trans('MainHeader.menuList.help.title')}
              </Button>
              {/* ヘルプメニューリスト */}
              <Menu
                id="mv-mainHeaderHelpMenuList"
                anchorEl={helpMenu}
                open={helpMenuOpen}
                onClose={helpClose}>
                <MenuItem
                  id="mv-linkManual"
                  onClick={() => {
                    navigate(routeList.mainManual.path);
                    helpClose();
                  }}>
                  {trans('MainHeader.menuList.help.item.manual')}
                </MenuItem>
                <MenuItem
                  id="mv-linkTermsOfService"
                  onClick={() => {
                    navigate(routeList.mainTermsOfService.path);
                    helpClose();
                  }}>
                  {trans('MainHeader.menuList.help.item.userTerms')}
                </MenuItem>
                <MenuItem
                  id="mv-linkPrivacyPolicy"
                  onClick={() => {
                    navigate(routeList.mainPrivacyPolicy.path);
                    helpClose();
                  }}>
                  {trans('MainHeader.menuList.help.item.privacyPolicy')}
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Grid>
        {/* アカウント */}
        <Grid item xs="auto">
          <Button
            color="secondary"
            id="mv-mainHeaderAccountBox"
            className="menuBox__account"
            aria-controls={accountMenuOpen ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            aria-expanded={accountMenuOpen ? 'true' : undefined}
            onClick={accountClick}
            startIcon={
              <img
                className="logoBox__icon--user"
                src={iconBlack.USER.src}
                alt={`${trans(
                  `MainHeader.image.alternativeText.${iconBlack.USER.alt}`
                )}`}
              />
            }
            endIcon={
              <img
                className="logoBox__icon--arrow"
                src={iconBlack.CHEVRON_DOWN.src}
                alt={`${trans(
                  `MainHeader.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                )}`}
              />
            }>
            {props.email}
          </Button>
          {/* アカウントメニューリスト */}
          <Menu
            id="mv-mainHeaderAccountMenuList"
            anchorEl={accountMenu}
            open={accountMenuOpen}
            onClose={accountClose}>
            <MenuItem onClick={() => onSignOut()} id="mv-menuLogout">
              {trans('MainHeader.menuList.account.item.logout')}
            </MenuItem>
            <MenuItem
              id="mv-linkChgEmail"
              onClick={() => {
                navigate(routeList.changeEmail.path);
                accountClose();
              }}>
              {trans('MainHeader.menuList.account.item.changeEmail')}
            </MenuItem>
            <MenuItem
              id="mv-linkChgPassword"
              onClick={() => {
                navigate(routeList.changePassword.path);
                accountClose();
              }}>
              {trans('MainHeader.menuList.account.item.changePassword')}
            </MenuItem>
            <MenuItem
              id="mv-linkNotifications"
              onClick={() => {
                navigate(routeList.notifications.path);
                accountClose();
              }}>
              {trans('MainHeader.menuList.account.item.notifications')}
            </MenuItem>
            <MenuItem
              id="mv-linkDisplaySetting"
              onClick={() => {
                navigate(routeList.displaySetting.path);
                accountClose();
              }}>
              {trans('MainHeader.menuList.account.item.logoSettings')}
            </MenuItem>
            <MenuItem
              id="mv-linkDelAccount"
              onClick={() => {
                navigate(routeList.deleteAccount.path);
                accountClose();
              }}>
              {trans('MainHeader.menuList.account.item.deleteAccount')}
            </MenuItem>
          </Menu>
        </Grid>
        {/* 言語切り替え */}
        <Grid item xs="auto">
          <TextFieldSelectBox
            choices={pullDownMenu.LANGUAGE_SETTINGS}
            value={language}
            setValue={changeLanguage}
          />
        </Grid>
        {/* MHIのロゴ */}
        <Grid item xs="auto" className="logoBox">
          {mhiLogo === '' ? (
            <Box className="logoBox__icon--mhiLogo"></Box>
          ) : (
            <img
              className="logoBox__icon--mhiLogo"
              src={mhiLogo}
              alt={`${trans('MainHeader.image.alternativeText.mhiLogo')}`}
              id="mv-mhiLogo"
            />
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
export default Header;
